<template>
  <ul
    v-show="isOpen"
    :id="id"
    ref="menuRef"
    class="dropdown"
    role="menu"
    :aria-labelledby="buttonId"
    @keydown="handleKeydown"
  >
    <li
      v-for="(subItem, index) in item.items"
      :key="index"
      class="sub-link-item"
      :class="{
        'sub-link-item--last': index === (item.items?.length || 1) - 1
      }"
    >
      <slot
        name="default"
        el-class="sub-link"
        :sub-item="subItem"
        :child-ref="setRef(index)"
      />
    </li>
  </ul>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'

import type { PublicMenuItem } from '@/service/__generated-api'

const props = defineProps<{
  item: PublicMenuItem
  id: string
  buttonId: string
  isOpen: boolean
}>()

const emit = defineEmits(['close-dropdown'])

const menuRef = ref<HTMLUListElement | null>(null)
const activeIndex = ref(-1)

type ItemRef = Element | ComponentPublicInstance | null

const itemRefs = ref<ItemRef[]>([])

const setRef = (index: number) => (itemRef: ItemRef) => {
  itemRefs.value[index] = itemRef
}

const numberOfItems = props.item.items ? props.item.items.length : 0

const getElementByIndex = (index: number) => {
  const element = itemRefs.value[index]

  if (element) {
    const targetElement = element instanceof Element ? element : element.$el

    if (targetElement != null) {
      return targetElement as HTMLElement
    }
  }

  return null
}

const focusItem = (index: number) => {
  if (index >= 0 && index < numberOfItems) {
    activeIndex.value = index
    getElementByIndex(index)?.focus()
  }
}

const focusFirstItem = () => {
  if (numberOfItems > 0) {
    focusItem(0)
  }
}

const focusLastItem = () => {
  const lastIndex = numberOfItems - 1
  if (lastIndex >= 0) {
    focusItem(lastIndex)
  }
}

const handleKeydown = (event: KeyboardEvent) => {
  if (numberOfItems === 0) {
    return
  }

  let newIndex = activeIndex.value

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      newIndex = newIndex < numberOfItems - 1 ? newIndex + 1 : 0
      focusItem(newIndex)
      break
    case 'ArrowUp':
      event.preventDefault()
      newIndex = newIndex > 0 ? newIndex - 1 : numberOfItems - 1
      focusItem(newIndex)
      break
    case 'Home':
      event.preventDefault()
      focusFirstItem()
      break
    case 'End':
      event.preventDefault()
      focusLastItem()
      break
    case 'Escape':
      event.preventDefault()
      emit('close-dropdown')
      break
    case 'Tab':
      emit('close-dropdown')
      break
  }
}

watch(
  () => props.item,
  () => {
    activeIndex.value = -1
  }
)

defineExpose({ focusFirstItem })
</script>

<style lang="scss" scoped>
.dropdown {
  position: absolute;
  display: grid;
  grid-template-rows: repeat(3, auto);
  grid-auto-flow: column;
  gap: rem(22px) rem(35px);
  padding-block: rem(31px) rem(35px);
  color: $dark-brown;
}
</style>
